import type { ReactNode, ChangeEvent } from 'react';
import { useRef, useState } from 'react';
import type { FieldValues, Path, RegisterOptions, PathValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SelectedFileDisplay from '@/components/common/SelectedFileDisplay';

/**
 * Option for RadioUpload, supports upload button.
 */
export interface RadioUploadOption {
    value: string;
    label: ReactNode;
    description?: string;
    icon?: ReactNode;
    id?: string;
    disabled?: boolean;
    upload?: boolean; // If true, show upload button when selected
}

/**
 * Props for RadioUpload component.
 */
export interface RadioUploadProps<TFormValues extends FieldValues = FieldValues> {
    name: Path<TFormValues>;
    options: RadioUploadOption[];
    label?: string;
    rules?: Omit<
        RegisterOptions<TFormValues, Path<TFormValues>>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
    defaultValue?: PathValue<TFormValues, Path<TFormValues>>;
    className?: string;
    required?: boolean;
    onFileChange?: (file: File | null) => void;
    onChange?: (value: string) => void;
    accept?: string;
    // Add these for uncontrolled usage
    value?: string;
    errorMessage?: string;
}

/**
 * RadioUpload
 * Renders a radio group with optional file upload for selected option.
 */
const RadioUpload = <TFormValues extends FieldValues = FieldValues>({
    name,
    label,
    options,
    defaultValue,
    className,
    required,
    onFileChange,
    onChange,
    accept = '.xlsx,.xls,.csv',
    value,
    errorMessage,
}: RadioUploadProps<TFormValues>) => {
    const { t } = useTranslation('common');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Track file per radio option (only for selected upload option)
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedValue, setSelectedValue] = useState<string>(defaultValue ? String(defaultValue) : '');

    /**
     * Handle file selection.
     */
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setSelectedFile(file);
        if (onFileChange) {
            onFileChange(file);
        }
    };

    /**
     * Remove the selected file.
     */
    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (onFileChange) {
            onFileChange(null);
        }
    };

    /**
     * Re-trigger file input.
     */
    const handleReUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle radio change for uncontrolled usage
    const handleRadioChange = (val: string) => {
        setSelectedValue(val);
        setSelectedFile(null);
        onChange?.(val);
    };

    // Use controlled value if provided, otherwise use local state
    const currentValue = value !== undefined ? value : selectedValue;

    return (
        <div className={className}>
            {label && (
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={name}>
                    {label}
                    {required && <span className="ml-1 text-red-500">*</span>}
                </label>
            )}
            <div className="flex flex-col gap-5 pt-2">
                {options.map(option => (
                    <div key={option.value} className="flex items-start gap-2">
                        <input
                            type="radio"
                            id={`${name}-${option.value}`}
                            value={option.value}
                            checked={currentValue === option.value}
                            onChange={e => handleRadioChange(e.target.value)}
                            className="mt-1 h-4 w-4 border-neutral-300 text-teal-600 focus:ring-teal-500"
                            disabled={option.disabled}
                        />
                        <label
                            htmlFor={`${name}-${option.value}`}
                            className="block flex-1 text-base font-medium text-neutral-800"
                            style={{
                                color: option.disabled ? 'gray' : 'inherit',
                                cursor: option.disabled ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <div className="flex w-full flex-col items-start gap-1">
                                <span className="font-semibold text-neutral-800">{option.label}</span>
                                {option.description && (
                                    <span className="text-base font-medium text-neutral-500">{option.description}</span>
                                )}
                                {/* Show upload UI if this option is selected and upload is true */}
                                {option.upload && currentValue === option.value && (
                                    <div className="mt-2 w-full">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            onChange={handleFileChange}
                                            accept={accept}
                                        />
                                        {!selectedFile ? (
                                            <button
                                                type="button"
                                                className="rounded border border-neutral-300 bg-white px-3 py-1 text-base font-semibold text-neutral-800 hover:bg-gray-50"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                {t('common:browseFile')}
                                            </button>
                                        ) : (
                                            <div className="w-full">
                                                <SelectedFileDisplay
                                                    file={selectedFile}
                                                    onReUpload={handleReUpload}
                                                    onRemove={handleRemoveFile}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </label>
                    </div>
                ))}
                {errorMessage && <span className="mt-1 text-xs text-red-500">{errorMessage}</span>}
            </div>
        </div>
    );
};

export default RadioUpload;
